// Sandbox debug overlay. Toggled with F2 while in-game. Shows:
//   • Current SANDBOX_CONFIG values
//   • Grounded state (true/false)
//   • Player world position
//   • Player vertical velocity
//   • Counts of visible vs hidden colliders (supportMeshes)
//
// Visible colliders are anything in `supportMeshes`. Hidden colliders
// are zero in pure sandbox mode by definition — historically the only
// "hidden" piece was the cloud-floor plane which we no longer render.

import { useEffect, useState } from "react";
import { SANDBOX_CONFIG } from "../engine/sandboxConfig";
import type { Game } from "../engine/Game";

interface Props {
  game: Game | null;
}

interface Snapshot {
  grounded: boolean;
  pos: { x: number; y: number; z: number };
  vy: number;
  supportCount: number;
}

export function SandboxDebugOverlay({ game }: Props) {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [, force] = useState(0);

  // F2 to toggle.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "F2") {
        SANDBOX_CONFIG.debugMode = !SANDBOX_CONFIG.debugMode;
        force((n) => n + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!SANDBOX_CONFIG.debugMode || !game) return;
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const s = game.debugSnapshot();
      if (s) setSnap(s);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [game]);

  if (!SANDBOX_CONFIG.debugMode) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 20,
        background: "rgba(0,0,0,0.78)",
        color: "#fff",
        font: "12px/1.4 ui-monospace, Menlo, Consolas, monospace",
        padding: "10px 14px",
        borderRadius: 10,
        border: "2px solid #6be09b",
        boxShadow: "0 4px 18px rgba(0,0,0,0.4)",
        pointerEvents: "none",
        textAlign: "left",
        minWidth: 280,
      }}
    >
      <div style={{ color: "#6be09b", fontWeight: 800, marginBottom: 6 }}>
        SANDBOX DEBUG (F2 to hide)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px" }}>
        <span style={{ opacity: 0.7 }}>enableFallDeath</span>
        <span>{String(SANDBOX_CONFIG.enableFallDeath)}</span>
        <span style={{ opacity: 0.7 }}>enableAutoRespawnOnFall</span>
        <span>{String(SANDBOX_CONFIG.enableAutoRespawnOnFall)}</span>
        <span style={{ opacity: 0.7 }}>enableInvisibleSafetyFloor</span>
        <span>{String(SANDBOX_CONFIG.enableInvisibleSafetyFloor)}</span>
        <span style={{ opacity: 0.7 }}>enableInvisibleBoundaryWalls</span>
        <span>{String(SANDBOX_CONFIG.enableInvisibleBoundaryWalls)}</span>
        <span style={{ opacity: 0.7 }}>visibleObjectsOnlyCollision</span>
        <span>{String(SANDBOX_CONFIG.visibleObjectsOnlyCollision)}</span>
        <span style={{ opacity: 0.7 }}>floatingLevelBaseY</span>
        <span>{SANDBOX_CONFIG.floatingLevelBaseY}</span>
      </div>
      <hr style={{ borderColor: "rgba(255,255,255,0.18)", margin: "8px 0" }} />
      {snap ? (
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px" }}>
          <span style={{ opacity: 0.7 }}>grounded</span>
          <span style={{ color: snap.grounded ? "#6be09b" : "#ff7a7a" }}>
            {String(snap.grounded)}
          </span>
          <span style={{ opacity: 0.7 }}>pos</span>
          <span>
            x {snap.pos.x.toFixed(2)} &nbsp; y {snap.pos.y.toFixed(2)} &nbsp; z {snap.pos.z.toFixed(2)}
          </span>
          <span style={{ opacity: 0.7 }}>velocityY</span>
          <span>{snap.vy.toFixed(2)}</span>
          <span style={{ opacity: 0.7 }}>visible colliders</span>
          <span>{snap.supportCount}</span>
          <span style={{ opacity: 0.7 }}>hidden colliders</span>
          <span>0</span>
        </div>
      ) : (
        <div style={{ opacity: 0.7 }}>waiting for game…</div>
      )}
    </div>
  );
}
